import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import { DI_TOKENS } from '@constants';
import { useInjection } from '@hooks';
import { AssessmentDomainStatus, FileStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { AssessmentWorkflowStore } from '@stores';

import { useCancelDomain, useSaveDomain } from '../api';
import { AssessmentDocumentViewModel } from '../types';
import { DomainDetailsViewModel } from '../utils/assessment-mapper';
import { getLocalizedErrorMessage } from '../utils/error-utils';

interface UseNavigationGuardProps {
  assessmentId: string;
  currentDomain?: DomainDetailsViewModel | null;
  files: AssessmentDocumentViewModel[];
  deletedFiles: Set<string>;
  noEvidenceChecked: boolean;
  isApplicable?: boolean;
}

export type NavigationType = 'back' | 'next' | 'exit' | 'domain' | 'resume' | 'resumeNext';

interface PendingNavigation {
  type: NavigationType;
  action: () => void | Promise<void>;
}

/**
 * Hook to guard navigation and prevent data loss from unsaved changes
 * 
 * Detects unsaved changes and prompts user before navigation:
 * - Pending document uploads
 * - Documents marked for deletion
 * - Changes in domain fields
 * 
 * Provides save/discard/cancel options before allowing navigation
 */
export type NavigationGuard = ReturnType<typeof useNavigationGuard>;

export const useNavigationGuard = ({
  assessmentId,
  currentDomain,
  files,
  deletedFiles,
  noEvidenceChecked,
  isApplicable = true,
}: UseNavigationGuardProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const saveDomainMutation = useSaveDomain();
  const cancelDomainMutation = useCancelDomain();
  const { t } = useTrans();
  const workflowStore = useInjection<AssessmentWorkflowStore>(
    DI_TOKENS.AssessmentWorkflowStore,
  );
  const isConfirmModalOpen = workflowStore.isSaveChangesModalOpen;
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Checks if current domain has unsaved changes
   */
  const hasUnsavedChanges = useCallback((): boolean => {
    if (!currentDomain || !files) return false;

    // Check if domain is not yet completed (might have unsaved changes)
    const isNotCompleted = currentDomain.status !== AssessmentDomainStatus.COMPLETED;

    // Check for uploading files (optimistic UI state)
    const hasUploadingFiles = files.some((file) => file.status === FileStatus.UPLOADING);

    // Check for pending documents (uploaded but not saved)
    const hasPendingDocuments = files.some((file) => file.status === FileStatus.NOT_SAVED);

    // Check for documents marked for deletion (confirmed-deleted stay in filesArray;
    // pending-deleted are filtered out of filesArray but tracked in deletedFiles Set)
    const hasDeletedDocuments =
      files.some((file) => file.status === FileStatus.DELETED) || deletedFiles.size > 0;

    // If any of these conditions are true, we have unsaved changes
    return (
      (isNotCompleted && (hasUploadingFiles || hasPendingDocuments || hasDeletedDocuments)) ||
      hasUploadingFiles
    );
  }, [currentDomain, files, deletedFiles]);

  /**
   * Saves current domain changes
   */
  const saveCurrentDomain = useCallback(async (): Promise<boolean> => {
    if (!assessmentId || !currentDomain?.id) {
      console.error('Cannot save: missing assessmentId or domainId');
      return false;
    }

    try {
      await saveDomainMutation.mutateAsync({
        params: {
          assessmentId,
          assessmentDomainId: currentDomain.id,
        },
        data: {
          isSkipped: noEvidenceChecked,
          isApplicable: isApplicable,
        },
      });

      enqueueSnackbar('Domain saved successfully', { variant: 'success' });
      return true;
    } catch {
      return false;
    }
  }, [
    assessmentId,
    currentDomain,
    noEvidenceChecked,
    isApplicable,
    saveDomainMutation,
    enqueueSnackbar,
    t,
  ]);

  /**
   * Discards current domain changes
   */
  const discardCurrentDomain = useCallback(async (): Promise<boolean> => {
    if (!assessmentId || !currentDomain?.id) {
      console.error('Cannot discard: missing assessmentId or domainId');
      return false;
    }

    try {
      await cancelDomainMutation.mutateAsync({
        assessmentId,
        assessmentDomainId: currentDomain.id,
      });

      enqueueSnackbar('Changes discarded', { variant: 'info' });
      return true;
    } catch (error) {
      console.error('Failed to discard changes:', error);
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to discard changes. Please try again.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }
  }, [assessmentId, currentDomain, cancelDomainMutation, enqueueSnackbar, t]);

  /**
   * Handles "Save & Continue" option from confirmation modal
   */
  const handleSaveAndMove = useCallback(async () => {
    if (!pendingNavigation) return;

    setIsProcessing(true);
    try {
      const saveSuccess = await saveCurrentDomain();
      if (saveSuccess) {
        workflowStore.closeSaveChangesModal();
        await pendingNavigation.action();
        setPendingNavigation(null);
      }
    } catch {
      // Swallowed deliberately: `saveCurrentDomain` already surfaced the failure
      // to the user, and this handler's only remaining job is to leave the
      // pending navigation in place so they can retry it.
    } finally {
      setIsProcessing(false);
    }
  }, [pendingNavigation, saveCurrentDomain, enqueueSnackbar, t]);

  /**
   * Handles "Discard & Continue" option from confirmation modal
   */
  const handleDiscardAndMove = useCallback(async () => {
    if (!pendingNavigation) return;

    setIsProcessing(true);
    try {
      const discardSuccess = await discardCurrentDomain();
      if (discardSuccess) {
        workflowStore.closeSaveChangesModal();
        await pendingNavigation.action();
        setPendingNavigation(null);
      }
    } catch (error) {
      console.error('Failed to discard and move:', error);
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to discard and move. Please try again.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  }, [pendingNavigation, discardCurrentDomain, enqueueSnackbar, t]);

  /**
   * Handles "Stay Here" option from confirmation modal
   */
  const handleStay = useCallback(() => {
    workflowStore.closeSaveChangesModal();
    setPendingNavigation(null);
  }, [workflowStore]);

  /**
   * Attempts to navigate with guard protection
   * If unsaved changes exist, shows confirmation modal
   * Otherwise, executes navigation immediately
   */
  const guardedNavigate = useCallback(
    async (action: () => void | Promise<void>, type: NavigationType = 'domain') => {
      if (hasUnsavedChanges()) {
        // Store pending navigation and show confirmation modal
        setPendingNavigation({ type, action });
        workflowStore.openSaveChangesModal();
      } else {
        // No unsaved changes, navigate immediately
        await action();
      }
    },
    [hasUnsavedChanges, workflowStore],
  );

  // Publish the modal's handlers into the store so the layout can render the
  // SaveChangesModal as an observer without prop-drilling from the page.
  useEffect(() => {
    workflowStore.registerSaveChangesModalHandlers({
      isLoading: isProcessing,
      destinationType: pendingNavigation?.type,
      onSaveAndMove: handleSaveAndMove,
      onDiscardAndMove: handleDiscardAndMove,
      onStay: handleStay,
    });
    return () => workflowStore.registerSaveChangesModalHandlers(null);
  }, [
    workflowStore,
    isProcessing,
    pendingNavigation?.type,
    handleSaveAndMove,
    handleDiscardAndMove,
    handleStay,
  ]);

  return {
    // State
    isConfirmModalOpen,
    isProcessing,
    pendingNavigationType: pendingNavigation?.type,
    hasUnsavedChanges: hasUnsavedChanges(),

    // Actions
    guardedNavigate,
    handleSaveAndMove,
    handleDiscardAndMove,
    handleStay,
  };
};
